import styled, { css, RuleSet } from 'styled-components'
import { Controller, useFormContext } from 'react-hook-form'
import { FormObjectType } from 'src/types/react-hook-form'
import React, { ChangeEvent, useRef } from 'react'
import { Noto_body1_b16, Noto_body1_r16, Noto_body2_m14 } from 'src/styles/fonts/NotoSansKrFonts'
import { Icon } from 'src/assets/svg/Icon'
import { Container } from 'src/components/common/Container'
import { Typography } from 'src/components/common/Typography'
import RenderIf from 'src/components/common/RenderIf'
import { useToast } from 'src/provider/ToastContextProvider'
import { useCookies } from 'react-cookie'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { requestSelectFile } from 'src/utils/bridge'

const InputComponent = ({
  style,
  tag,
  register_options,
  placeholder,
  disabled,
  type,
  topChildren,
  bottomChildren,
  formatDisplayValue,
  normalizeDisplayInput,
}: Partial<FormObjectType>) => {
  const { register, control } = useFormContext()
  const useDisplayFormat = formatDisplayValue && normalizeDisplayInput && tag

  if (useDisplayFormat) {
    const rules = {
      required: register_options?.required,
      min: register_options?.min,
      max: register_options?.max,
      maxLength: register_options?.maxLength,
      minLength: register_options?.minLength,
      pattern: register_options?.pattern,
      validate: register_options?.validate,
    }
    return (
      <Container $option="flex_column" $row_gap={'6px'}>
        {topChildren}
        <Controller
          name={tag}
          control={control}
          rules={rules}
          render={({ field, fieldState }) => (
            <CustomInput
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              value={formatDisplayValue(field.value ?? '')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = normalizeDisplayInput(e.target.value)
                field.onChange(next)
                register_options?.onChange?.(e)
              }}
              placeholder={placeholder}
              disabled={disabled}
              type={type}
              $style={style}
              error={fieldState.error?.message}
            />
          )}
        />
        {bottomChildren}
      </Container>
    )
  }

  const customInputProps = {
    ...register(tag!, register_options),
    placeholder: placeholder,
    disabled: disabled,
    type: type,
    $style: style,
  }

  return (
    <Container $option="flex_column" $row_gap={'6px'}>
      {topChildren}
      <CustomInput {...customInputProps} />
      {bottomChildren}
    </Container>
  )
}

const DatePickerComponent = ({
  tag,
  style,
  register_options,
  placeholder,
  topChildren,
  bottomChildren,
}: Partial<FormObjectType>) => {
  const { control } = useFormContext()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.focus()
      fileInputRef.current?.showPicker?.()
    }
  }

  return (
    <Container $option="flex_column" $row_gap={'6px'}>
      {topChildren}
      <Controller
        name={tag as string}
        control={control}
        rules={{ required: `${register_options?.required}` }}
        render={({ field }) => {
          const initialValue = field.value === ''

          return (
            <Container
              onClick={handleClick}
              $style={css`
                position: relative;
              `}
            >
              <RenderIf when={initialValue}>
                <DatePickerPlaceHolder $fontStyle={Noto_body1_r16} $color={'var(--black-40)'}>
                  {placeholder}
                </DatePickerPlaceHolder>
              </RenderIf>
              <CustomDatePicker
                {...field}
                onChange={register_options!?.onChange}
                $style={style}
                $initialValue={initialValue}
                ref={fileInputRef}
              />
              <DatePickerSelectedDateValue>
                <Typography color={`var(--black-90)`} $fontStyle={Noto_body1_r16}>
                  {field.value}
                </Typography>
              </DatePickerSelectedDateValue>
            </Container>
          )
        }}
      />
      {bottomChildren}
    </Container>
  )
}

const TextAreaComponent = ({
  style,
  tag,
  register_options,
  type,
  topChildren,
  bottomChildren,
  placeholder,
}: Partial<FormObjectType>) => {
  const formContext = useFormContext()

  const customTextAreaProps = {
    ...formContext.register(tag!, register_options),
    type: type,
    $style: style,
  }
  return (
    <Container $option="flex_column" $row_gap="6px">
      {topChildren}
      <CustomTextArea {...customTextAreaProps} placeholder={placeholder} />
      {bottomChildren}
    </Container>
  )
}

const AgencyRadioComponent = ({
  tag,
  register_options,
  optionValueList,
  disabled,
}: Partial<FormObjectType>) => {
  const formContext = useFormContext()

  return (
    <CustomAgencyRadioWrap>
      {optionValueList?.map((element: { value: number; text: string }) => (
        <React.Fragment key={element.text}>
          <CustomAgencyLabel
            htmlFor={`radio-${element.value}`}
            id={`label-${element.value}`}
            $active={`${formContext.watch(tag!)?.toString() === element.value?.toString()}`}
            $disabled={disabled}
          >
            <CustomAgencyLabelText>{element.text}</CustomAgencyLabelText>
            <CustomRadio
              id={`radio-${element.value}`} // 라디오 버튼의 id를 변경
              value={parseInt(`${element.value}`, 10)}
              {...formContext.register(tag!, register_options)}
            />
          </CustomAgencyLabel>
        </React.Fragment>
      ))}
    </CustomAgencyRadioWrap>
  )
}

const CheckBoxComponent = ({
  style,
  tag,
  register_options,
  topChildren,
  bottomChildren,
}: Partial<FormObjectType>) => {
  const { register, watch } = useFormContext()
  return (
    <React.Fragment>
      {topChildren}
      <CustomCheckLabel $style={style}>
        <HiddenCheckbox
          {...register(tag!, register_options)}
          onClick={register_options!?.onClick}
        />
        <StyledCheckbox active={`${watch(`${tag}`)}`}>
          <Icon.Check />
        </StyledCheckbox>
      </CustomCheckLabel>
      {bottomChildren}
    </React.Fragment>
  )
}

const SelectComponent = ({
  style,
  optionValueList,
  register_options,
  tag,
  placeholder,
  topChildren,
  bottomChildren,
}: Partial<FormObjectType>) => {
  const { register, getValues } = useFormContext()
  return (
    <Container $option="flex_column" $row_gap="6px">
      {topChildren}
      <CustomSelect
        {...register(tag!, register_options)}
        $style={style}
        $color={`var(${getValues(tag!) === '' ? '--black-40' : '--black-90'})`}
      >
        <CustomOption value="" disabled hidden>
          {placeholder}
        </CustomOption>
        {optionValueList?.map(({ text, value }) => (
          <CustomOption value={value} key={value}>
            {text}
          </CustomOption>
        ))}
      </CustomSelect>
      {bottomChildren}
    </Container>
  )
}

const FileUploaderComponent = ({
  style,
  tag,
  type,
  register_options,
  bottomChildren,
}: Partial<FormObjectType>) => {
  const { openToast } = useToast()
  const { aos } = useChannel()
  const { getValues, setError, resetField, register, setValue } = useFormContext()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const initialValue = getValues(tag as string)?.name === '' || !getValues(tag as string)?.name
  const fileUploadClickHandler = () => fileInputRef.current?.click()
  const resetFileValueHandler = () => {
    resetField(tag!)
    if (fileInputRef.current) {
      fileInputRef.current.value = '' // 입력 필드의 값을 빈 문자열로 초기화
    }
  }

  const fileSelectHandler = () => {
    if (getValues(tag as string)?.name) return

    requestSelectFile(fileUploadClickHandler)
      .then((res) => {
        const { fileName, base64File } = res
        if (res === null) return
        switch (base64File) {
          case '':
            break

          case '확장자오류':
          case 'error_type':
            resetFileValueHandler()
            setError(tag as string, {
              type: 'validate',
              message: aos
                ? 'pdf 또는 이미지 파일만 업로드 가능합니다.'
                : '이미지 파일만 업로드 가능합니다.',
            })

            break
          case '5MB/용량초과':
          case 'error_size':
            resetFileValueHandler()
            setError(tag as string, {
              type: 'validate',
              message: '5MB 이하 파일만 업로드 가능합니다.',
            })
            break
          case 'error':
            openToast({ contents: '파일을 찾을 수 없습니다.' })
            break
          default:
            setValue(
              tag as string,
              { file: base64File as string, name: fileName as string },
              { shouldValidate: true, shouldTouch: true },
            )
            break
        }
      })
      .catch((error) => {
        console.warn(error)
      })
  }

  return (
    <Container $option="flex_column" $row_gap={'6px'}>
      <CustomFileUploader $style={style}>
        <button onClick={fileSelectHandler} disabled={!initialValue}>
          <Typography
            $fontStyle={Noto_body2_m14}
            $color={`var(${initialValue ? '--black-60' : '--black-30'})`}
          >
            파일선택
          </Typography>
          <Icon.Pin color={`var(${initialValue ? '--black-70' : '--black-30'})`} />
        </button>
        <div>
          <span>{initialValue ? `업로드 대기중\u00A0\u00A0` : getValues(tag as string)?.name}</span>
          <RenderIf when={!initialValue}>
            <Icon.GrayCircleX onClickFn={resetFileValueHandler} size="24" />
          </RenderIf>
        </div>
        <CustomInput
          {...register(tag!, register_options)}
          type={type}
          ref={fileInputRef}
          accept=".pdf, .jpg, .png, .jpeg, .bmp, .gif, .tif, .tiff, .svg"
          value={undefined}
          $style={css`
            display: none;
            width: 0;
          `}
        />
      </CustomFileUploader>
      {bottomChildren}
    </Container>
  )
}

export {
  InputComponent,
  SelectComponent,
  AgencyRadioComponent,
  CheckBoxComponent,
  FileUploaderComponent,
  DatePickerComponent,
  TextAreaComponent,
}

const CustomRadio = styled.input.attrs({ type: 'radio' })`
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  height: 48px;
`
const CustomAgencyRadioWrap = styled.section`
  width: 100%;
  display: flex;
`

const CustomAgencyLabel = styled.label<{ $active: string; id: string; $disabled: boolean }>`
  text-align: center;
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  height: 48px;

  &:not(:first-child) {
    margin-left: -1px;
  }

  border-radius: ${(props) => {
    switch (props.id) {
      case 'label-0':
        return '12px 0 0 12px'
      case 'label-1':
        return '0'
      case 'label-2':
      default:
        return '0 12px 12px 0'
    }
  }};
  ${(props) =>
    props.$active === 'false' &&
    css`
      ${Noto_body1_r16};
      color: var(--black-50);
      border: 1px solid var(--black-20);
      background-color: var(--white);
    `}

  ${(props) =>
    props.$disabled &&
    css`
      pointer-events: none;
      color: var(--black-20);
      border: 1px solid var(--black-20);
      background-color: var(--black-2);
    `}
     
  ${(props) =>
    props.$active === 'true' &&
    css`
      z-index: 1;
      ${Noto_body1_b16};
      color: var(--brand-50);
      border: 1px solid var(--brand-40);
      background-color: var(--brand-5);
    `}
`
const CustomAgencyLabelText = styled.span`
  width: 100%;
  height: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CustomCheckLabel = styled.label<{ $style?: RuleSet<object> }>`
  ${(props) => props.$style};
  width: 26px;
  height: 26px;
  cursor: pointer;
`
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 26px;
  width: 26px;
`
const StyledCheckbox = styled.div<{ active: string }>`
  display: flex;
  height: 26px;
  width: 26px;
  min-width: 26px;
  background-color: ${(props) => (props.active === 'true' ? 'var(--brand-50)' : 'white')};
  border-radius: 6px;
  flex-direction: column;
  justify-content: center;
  position: relative;
  outline: ${(props) => (props.active === 'true' ? 'unset' : `1px solid var(--black-20)`)};

  > svg {
    margin: 0 auto;
  }
`

const CustomSelect = styled.select<{ $style?: RuleSet<object>; $color: string }>`
  ${(props) => props.$style};
  color: ${(props) => props.$color};
  option {
    width: fit-content;
  }
`
const CustomOption = styled.option`
  width: 100%;
`

const CustomFileUploader = styled.div<{ $style?: RuleSet<object> }>`
  ${(props) => props.$style};
`
const CustomInput = styled.input<{ error?: string; $style?: RuleSet<object> }>`
  width: 100%;
  ${(props) => props.$style};
`

const CustomDatePicker = styled.input.attrs({ type: 'date' })<{
  $style?: RuleSet<object>
  $initialValue: boolean
}>`
  width: 100%;
  ${(props) => props.$style};
  color: transparent !important;
  text-indent: -9999px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  &::-webkit-date-and-time-value {
    display: none;
  }
  &::-webkit-datetime-edit-text,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-year-field,
  &::-webkit-datetime-edit-day-field {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-appearance: ${(props) => (props.$initialValue ? 'none' : 'inline-block')};
    display: ${(props) => (props.$initialValue ? 'none' : 'inline-block')};
  }
  z-index: 0;
  &::-webkit-calendar-picker-indicator {
    display: flex;

    background-color: transparent;
  }
`

const CustomTextArea = styled.textarea<{ error?: string; $style?: RuleSet<object> }>`
  width: 100%;
  ${(props) => props.$style};
`

const DatePickerPlaceHolder = styled(Typography)`
  display: flex;
  top: 12px;
  left: 16px;
  position: relative;
  height: 0;
`

const DatePickerSelectedDateValue = styled.div`
  position: absolute;
  width: 100%;
  z-index: 1;
  top: 0;
  > span {
    align-items: center;
    width: fit-content;
    position: absolute;
    left: 15px;
    height: 48px;
    display: flex;
  }
`
